import { is, List } from "immutable";
import * as React from "react";
import * as BS from "react-bootstrap";
import * as BT from "react-bootstrap-table";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import { Statuses } from "../../../domain";
import * as M from "../../../models";
import { AppState } from "../../../store";
import { Utility } from "../../../utils";

type DataRow = M.Event.EventAdminListItem & {
    endDateValue: number;
    startDateValue: number;
    status: string;
};

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultListViewStatePropsMap(state, ownProps),
    events: state.adminEvents.listEvents,
    loadingEvents: state.adminEvents.loadingListEvents,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadEvents: () => dispatch(Actions.AdminEvent.loadListEvents()),
    removeEvent: (evt: M.Event.EventAdminListItem) => dispatch(Actions.AdminEvent.removeEvent(evt)),
    toggleEventsLoading: (loading: boolean) => dispatch(Actions.AdminEvent.toggleLoadingListEvents(loading)),
});

type OwnProps = RouteComponentProps<{}> & {
};

type ConnectedState = Comp.Base.DefaultListViewConnectedState & {
    events: List<M.Event.EventAdminListItem>;
    loadingEvents: boolean;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadEvents: () => void;
    removeEvent: (evt: M.Event.EventAdminListItem) => void;
    toggleEventsLoading: (loading: boolean) => void;
};

interface OwnState {
    events: List<DataRow>;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class ListComponent extends Comp.Base.BaseListViewComponent<CombinedProps, OwnState> {

    public constructor(props: any) {
        super(props);
        this.state = {
            ...this.getInitialListViewState(props),
            events: List(),
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        await this.initializeComponent();
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (!is(prevProps.events, this.props.events))
            this.loadEventsFromState();
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const loading = this.state.loading || !!this.props.loadingEvents;

        const actionsPrimary = <BS.ButtonToolbar>
            <BS.ButtonGroup>
                <Link className="btn btn-default" to={Paths.AdminEventEdit(0)}>
                    <Comp.Generic.Icon icon="plus" /> Add event
                </Link>
            </BS.ButtonGroup>
        </BS.ButtonToolbar>;

        const tableActions = this.getTableActions({
            dataLength: this.state.events.size,
            filteredLength: this.state.events.size,
            searchPlaceholder: "Search events...",
        });

        return <Comp.Page.AdminPage actionsPrimary={actionsPrimary} actionsSecondary={tableActions} title={"Events"}>

            {this.getErrorAlert()}

            <Comp.Generic.LoadingSpinner show={loading} />

            <BT.BootstrapTable
                bordered={false}
                data={this.state.events.toArray()}
                pagination={this.state.tablePageSize > -1}
                options={{
                    afterSearch: (searchText, results) => this.onTableSearch(searchText, results),
                    page: this.state.tablePage,
                    paginationPanel: () => <div />,
                    sizePerPage: this.state.tablePageSize,
                    onSortChange: (sortName: any, sortOrder: any) => this.handleTableSort(sortOrder, sortName),
                    defaultSortName: "startDateValue",
                    defaultSortOrder: "desc",
                }}
                ref={(ref: any) => this.table = ref}
            >
                <BT.TableHeaderColumn dataField="id" isKey width="5%">ID</BT.TableHeaderColumn>

                <BT.TableHeaderColumn
                    dataField="title"
                    dataFormat={(cell: any, row: DataRow) => (<Link to={Paths.AdminEventEdit(row.id)}>{row.title}</Link>)}
                    dataSort
                >Title</BT.TableHeaderColumn>

                <BT.TableHeaderColumn dataField="type" dataSort width="15%">Type</BT.TableHeaderColumn>

                <BT.TableHeaderColumn
                    dataField="abstractsEnabled"
                    dataFormat={(cell: any, row: DataRow) => row.abstractsEnabled ? "Yes" : ""}
                    dataSort
                    width="15%"
                >Abstracts enabled?</BT.TableHeaderColumn>

                <BT.TableHeaderColumn
                    dataField="startDateValue"
                    dataFormat={(cell: any, row: DataRow) => Utility.fromApiDateAsMoment(row.startDate).format("MM/DD/YYYY")}
                    dataSort
                    width="12%"
                >Starts on</BT.TableHeaderColumn>

                <BT.TableHeaderColumn
                    dataField="endDateValue"
                    dataFormat={(cell: any, row: DataRow) => Utility.fromApiDateAsMoment(row.endDate).format("MM/DD/YYYY")}
                    dataSort
                    width="12%"
                >Ends on</BT.TableHeaderColumn>

                <BT.TableHeaderColumn dataField="status" dataSort width="7%">Status</BT.TableHeaderColumn>

                <BT.TableHeaderColumn
                    dataField="id"
                    dataFormat={(cell: any, row: DataRow) => (
                        <Link to={Paths.AdminEventCalendarEdit(row.id)} title="Open calendar">
                            <Comp.Generic.Icon icon="calendar" />
                        </Link>
                    )}
                    dataSort
                    width="35px"
                />
            </BT.BootstrapTable>

        </Comp.Page.AdminPage>;
    }

    private async initializeComponent() {
        await this.loadEventsFromState(true);
        this.props.loadEvents();
    }

    private async loadEventsFromState(toggleLoading: boolean = false) {
        const events: List<DataRow> = this.props.events
            .map((x: M.Event.EventAdminListItem) => ({
                ...x,
                endDateValue: Utility.utcToLocal(x.endDate).valueOf(),
                startDateValue: Utility.utcToLocal(x.startDate).valueOf(),
                status: Statuses.nameOf(x),
            }))
            .toList();

        await this.setStateAsync({ ...this.state, events });

        if (!events.size && toggleLoading)
            this.props.toggleEventsLoading(true);

        // update table state from location state if available
        this.updateTableState(events);
    }
}

export const ListEvents: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(ListComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/adminEvent/list/List.tsx